import { SUPER_ADMIN_BASE_SERVICE } from "../../utils/ApiUrls";

class SuperAdminAuditService {

  /**
   * Get all audit programs across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by program name or description
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by program status
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit programs list
   */
  getAllAuditPrograms(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-audit-programs/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit programs (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit programs (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all audit programs:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific audit program
   * @param {string} accessToken - JWT access token
   * @param {number|string} programId - Audit Program ID
   * @returns {Promise} Promise resolving to audit program details
   */
  getAuditProgramDetails(accessToken, programId) {
    if (!programId) {
      return Promise.reject(new Error('Audit Program ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/audit-programs/details/?id=${programId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit program details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit program details (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit program details:', error);
        throw error;
      });
  }

  /**
   * Get audit program history with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number|string} params.id - Audit Program ID (optional - if not provided, returns all program history)
   * @param {string} params.search - Search in description and program title
   * @param {string} params.action_type - Filter by action type
   * @param {string} params.organization - Filter by organization ID
   * @param {string} params.ordering - Sort by 'newest' or 'oldest' (default: newest)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit program history
   */
  getAuditProgramHistory(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.id) queryParams.append('id', params.id);
    if (params.search) queryParams.append('search', params.search);
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/audit-programs/history/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit program history (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit program history (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit program history:', error);
        throw error;
      });
  }

  // -------------------------------------------Audit plans-----------------------------------




  /**
   * Get all audit plans across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by plan name or description
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by plan status
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit plans list
   */
  getAllAuditPlans(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-audit-plans/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit plans (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit plans (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all audit plans:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific audit plan
   * @param {string} accessToken - JWT access token
   * @param {number|string} planId - Audit Plan ID
   * @returns {Promise} Promise resolving to audit plan details
   */
  getAuditPlanDetails(accessToken, planId) {
    if (!planId) {
      return Promise.reject(new Error('Audit Plan ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/audit-plans/details/?id=${planId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit plan details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit plan details (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit plan details:', error);
        throw error;
      });
  }



  
  /**
   * Get audit plan history with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number|string} params.id - Audit Plan ID (optional - if not provided, returns all plan history)
   * @param {string} params.search - Search in description and plan title
   * @param {string} params.action_type - Filter by action type
   * @param {string} params.organization - Filter by organization ID
   * @param {string} params.ordering - Sort by 'newest' or 'oldest' (default: newest)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit plan history
   */
  getAuditPlanHistory(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.id) queryParams.append('id', params.id);
    if (params.search) queryParams.append('search', params.search);
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/audit-plans/history/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit plan history (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit plan history (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit plan history:', error);
        throw error;
      });
  }

  // ======================Audit Sections=========================================================

  /**
   * Get all audit sections across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by section name
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit sections list
   */
  getAllAuditSections(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-audit-sections/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit sections (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit sections (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all audit sections:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific audit section
   * @param {string} accessToken - JWT access token
   * @param {number|string} sectionId - Audit Section ID
   * @returns {Promise} Promise resolving to audit section details
   */
  getAuditSectionDetails(accessToken, sectionId) {
    if (!sectionId) {
      return Promise.reject(new Error('Audit Section ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/audit-sections/details/?id=${sectionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit section details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit section details (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit section details:', error);
        throw error;
      });
  }

  

  /**
   * Get audit section history with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number|string} params.id - Audit Section ID (optional - if not provided, returns all section history)
   * @param {string} params.search - Search in description, section title, and plan title
   * @param {string} params.action_type - Filter by action type
   * @param {string} params.organization - Filter by organization ID
   * @param {string} params.ordering - Sort by 'newest' or 'oldest' (default: newest)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit section history
   */
  getAuditSectionHistory(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.id) queryParams.append('id', params.id);
    if (params.search) queryParams.append('search', params.search);
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/audit-sections/history/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit section history (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit section history (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit section history:', error);
        throw error;
      });
  }



// ====================================Questions=============================================


  /**
   * Get all audit questions across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by question text
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by question status
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit questions list
   */
  getAllAuditQuestions(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-audit-questions/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit questions (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit questions (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all audit questions:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific audit question
   * @param {string} accessToken - JWT access token
   * @param {number|string} questionId - Audit Question ID
   * @returns {Promise} Promise resolving to audit question details
   */
  getAuditQuestionDetails(accessToken, questionId) {
    if (!questionId) {
      return Promise.reject(new Error('Audit Question ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/audit-questions/details/?id=${questionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit question details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit question details (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit question details:', error);
        throw error;
      });
  }


  
  /**
   * Get audit question history with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number|string} params.id - Audit Question ID (optional - if not provided, returns all question history)
   * @param {string} params.search - Search in description and question text
   * @param {string} params.action_type - Filter by action type
   * @param {string} params.organization - Filter by organization ID
   * @param {string} params.ordering - Sort by 'newest' or 'oldest' (default: newest)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated audit question history
   */
  getAuditQuestionHistory(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.id) queryParams.append('id', params.id);
    if (params.search) queryParams.append('search', params.search);
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/audit-questions/history/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch audit question history (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch audit question history (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching audit question history:', error);
        throw error;
      });
  }




// ======================================Recommendations=============================================

  /**
   * Get all recommendation actions across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by recommendation text
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by recommendation status
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated recommendation actions list
   */
  getAllRecommendationActions(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-recommendation-actions/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch recommendation actions (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch recommendation actions (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all recommendation actions:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific recommendation action
   * @param {string} accessToken - JWT access token
   * @param {number|string} recommendationId - Recommendation Action ID
   * @returns {Promise} Promise resolving to recommendation action details
   */
  getRecommendationActionDetails(accessToken, recommendationId) {
    if (!recommendationId) {
      return Promise.reject(new Error('Recommendation Action ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/recommendation-actions/details/?id=${recommendationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch recommendation action details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch recommendation action details (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching recommendation action details:', error);
        throw error;
      });
  }


  
  /**
   * Get recommendation action history (includes followup history) with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number|string} params.id - Recommendation Action ID (optional - if not provided, returns all recommendation history)
   * @param {string} params.search - Search in description and recommendation text
   * @param {string} params.action_type - Filter by action type
   * @param {string} params.organization - Filter by organization ID
   * @param {string} params.ordering - Sort by 'newest' or 'oldest' (default: newest)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated recommendation action history (includes followup_history if id is provided)
   */
  getRecommendationActionHistory(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.id) queryParams.append('id', params.id);
    if (params.search) queryParams.append('search', params.search);
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/recommendation-actions/history/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch recommendation action history (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch recommendation action history (${response.status})`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching recommendation action history:', error);
        throw error;
      });
  }

}

// Export a single instance
const superAdminAuditService = new SuperAdminAuditService();
export default superAdminAuditService;

